import { useMutation } from "@tanstack/react-query";
import { submitProfileContactForm } from "api-client";
import { useState } from "react";

import { type QuestionnaireFormAnswer } from "src/components/questionnaireSchema";

interface ContactFormState {
  open: boolean;
  submitted: boolean;
  name: string;
  email: string;
  website: string;
  turnstileToken: string | null;
  turnstileResetKey: number;
  submissionError: boolean;
  healthDataConsent: boolean;
  healthDataConsentError: boolean;
}

const initialState: ContactFormState = {
  open: false,
  submitted: false,
  name: "",
  email: "",
  website: "",
  turnstileToken: null,
  turnstileResetKey: 0,
  submissionError: false,
  healthDataConsent: false,
  healthDataConsentError: false,
};

interface UsePublicProfileContactFormParams {
  slug: string;
  isPreview: boolean;
  turnstileSiteKey: string;
  consentLocale: "en" | "pl";
  requiresHealthDataConsent: boolean;
}

export function usePublicProfileContactForm(
  params: UsePublicProfileContactFormParams,
) {
  const [state, setState] = useState<ContactFormState>(initialState);
  const submitMutation = useMutation({
    mutationFn: async (answers: QuestionnaireFormAnswer[]) => {
      await submitProfileContactForm({
        path: { slug: params.slug },
        body: {
          name: state.name,
          email: state.email,
          answers,
          turnstileToken: state.turnstileToken ?? undefined,
          website: state.website,
          healthDataConsent: params.requiresHealthDataConsent
            ? state.healthDataConsent
            : undefined,
          healthDataConsentLocale: params.requiresHealthDataConsent
            ? params.consentLocale
            : undefined,
        },
      });
    },
    onSuccess: () => {
      setState((current) => ({ ...current, submitted: true }));
    },
    onError: () => {
      setState((current) => ({
        ...current,
        submissionError: true,
        turnstileToken: null,
        turnstileResetKey: current.turnstileResetKey + 1,
      }));
    },
  });

  function setField<Key extends keyof ContactFormState>(
    field: Key,
    value: ContactFormState[Key],
  ) {
    setState((current) => ({ ...current, [field]: value }));
  }

  function handleOpenChange(open: boolean) {
    if (!open && submitMutation.isPending) {
      return;
    }

    if (!open) {
      setState((current) => ({
        ...initialState,
        name: current.name,
        email: current.email,
        website: current.website,
      }));
      return;
    }

    submitMutation.reset();
    setState((current) => ({
      ...current,
      open: true,
      turnstileResetKey: current.turnstileResetKey + 1,
    }));
  }

  function handleSubmit(answers: QuestionnaireFormAnswer[]) {
    setField("submissionError", false);

    if (params.requiresHealthDataConsent && !state.healthDataConsent) {
      setField("healthDataConsentError", true);
      return;
    }

    if (params.isPreview) {
      setField("submitted", true);
      return;
    }

    if (params.turnstileSiteKey && !state.turnstileToken) {
      setField("submissionError", true);
      return;
    }

    submitMutation.mutate(answers);
  }

  return {
    state,
    setField,
    handleOpenChange,
    handleSubmit,
    isPending: submitMutation.isPending,
  };
}
